import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  Title,
  Tooltip,
  Legend,
  type ChartData,
  type ChartOptions,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Chart } from "react-chartjs-2";
import { InfoTooltip } from "./InfoTooltip";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

export interface OccupancyAttritionDataItem {
  period: number;
  inventory: number;
  occupancyPct: number;
  attritionPct: number;
}

interface OccupancyAttritionChartProps {
  title?: string;
  tooltipText?: string;
  items: OccupancyAttritionDataItem[];
}

export function OccupancyAttritionChart({
  title = "DU Occupancy & Attrition by Period",
  tooltipText,
  items,
}: OccupancyAttritionChartProps) {
  const maxInventory = Math.max(...items.map((item) => item.inventory), 0);
  const yMax = Math.ceil((maxInventory * 1.15) / 5000) * 5000;

  const labels = items.map((item) => `Period ${item.period}`);

  const data: ChartData<"bar" | "line", number[], string> = {
    labels,
    datasets: [
      {
        type: "bar" as const,
        label: "Inventory",
        data: items.map((item) => item.inventory),
        backgroundColor: "#26890D66",
        barThickness: 12,
        maxBarThickness: 16,
        categoryPercentage: 0.6,
        barPercentage: 0.7,
        pointStyle: "rect",
        yAxisID: "y",
        order: 2,
        datalabels: {
          anchor: "end",
          align: "end",
          offset: 2,
          formatter: (value: number) => value.toLocaleString(),
          font: { size: 9, weight: 600 },
          color: "#000",
        },
      },
      {
        type: "line" as const,
        label: "Occupancy % (based on inventory)",
        data: items.map((item) => item.occupancyPct),
        borderColor: "#00A3E0",
        borderWidth: 2,
        pointBackgroundColor: "#00A3E0",
        pointRadius: 0,
        pointStyle: "line",
        fill: false,
        yAxisID: "y1",
        order: 1,
        datalabels: {
          anchor: "end",
          align: "top",
          offset: 4,
          formatter: (value: number) => `${value.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1")} %`,
          font: { size: 9, weight: 600 },
          color: "#000",
        },
      },
      {
        type: "line" as const,
        label: "Attrition % (based on actuals)",
        data: items.map((item) => Math.abs(item.attritionPct)),
        borderColor: "#ED8B00",
        borderWidth: 2,
        pointBackgroundColor: "#ED8B00",
        pointRadius: 0,
        pointStyle: "line",
        fill: false,
        yAxisID: "y1",
        order: 1,
        datalabels: {
          anchor: "end",
          align: "top",
          offset: 4,
          formatter: (_value: number, context: { dataIndex: number }) => {
            const original = items[context.dataIndex]?.attritionPct ?? 0;
            return `${original.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1")} %`;
          },
          font: { size: 9, weight: 600 },
          color: "#000",
        },
      },
    ],
  };

  const options: ChartOptions<"bar" | "line"> = {
    responsive: true,
    layout: {
      padding: {
        top: 20,
      },
    },
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        position: "bottom",
        align: "start",
        labels: {
          usePointStyle: true,
          boxWidth: 14,
          boxHeight: 10,
          padding: 18,
          color: "#000",
        },
      },
      title: {
        display: false,
        text: title,
      },
      datalabels: {
        clip: false,
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const datasetLabel = context.dataset.label ?? "";
            const value = context.parsed.y ?? 0;
            if (datasetLabel === "Attrition % (based on actuals)") {
              const original = items[context.dataIndex]?.attritionPct ?? 0;
              return `${datasetLabel}: ${original}%`;
            }
            if (datasetLabel.includes("%")) {
              return `${datasetLabel}: ${value}%`;
            }
            return `${datasetLabel}: ${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        type: "linear",
        position: "left",
        beginAtZero: true,
        min: 0,
        max: yMax,
        ticks: {
          stepSize: 5000,
        },
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: "Inventory",
        },
      },
      y1: {
        display: false,
        type: "linear",
        position: "right",
        beginAtZero: true,
        min: 0,
        max: 100,
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  return (
    <div className="chart-box">
      <header className="box-header">
        <div className="box-title">
          <span>{title}</span>
          {tooltipText && <InfoTooltip text={tooltipText} />}
        </div>
      </header>
      <Chart type="bar" data={data} options={options} />
    </div>
  );
}

 